import { ListFilter, RefreshCw, TriangleAlert } from "lucide-react"
import * as React from "react"

import { SortHeader, type SortState } from "@/components/rules/sort-header"
import { Button } from "@/components/ui/button"
import { Inactive } from "@/components/ui/inactive"
import { Segmented } from "@/components/ui/segmented"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableNumeric,
  TableRow,
} from "@/components/ui/table"
import {
  listStats,
  PAGE_SIZE,
  PERIODS,
  type Period,
  type RuleStat,
  type RuleType,
  type StatsQuery,
} from "@/lib/api"
import { formatDateFull, formatInt, formatPercent } from "@/lib/format"

const ALL = "__all__"

/** До низа окна осталось меньше — пора просить следующую порцию. */
const LOAD_AHEAD_PX = 160

type Filters = Omit<StatsQuery, "page" | "page_size">

interface Loaded {
  /** Последняя загруженная страница ручки. */
  page: number
  pages: number
}

const rowKey = (stat: RuleStat) => `${stat.rule_type}/${stat.seon_id}`

/** Тип правила — закрытый словарь модели (`RULE_TYPES`), а не значения из ответа. */
const RULE_TYPES: Array<{ value: RuleType; label: string }> = [
  { value: "default", label: "Default" },
  { value: "custom", label: "Custom" },
  { value: "local", label: "Local" },
]

const PERIOD_OPTIONS = [
  // `Custom` в образце открывает выбор произвольного интервала. Интервала за
  // ним нет — ни в ручке, ни в данных, — поэтому кнопка на месте, но выключена.
  { value: "custom" as const, label: "Custom", disabled: true, title: "Custom date ranges are not available in this console." },
  ...PERIODS.map((period) => ({ value: period, label: period })),
]

/**
 * Панель «Applied rules statistics» — на `/` и во вкладке Scoring → Dashboard
 * один и тот же компонент.
 *
 * Цифры — только из ручки: срабатывания считаются в ClickHouse по решениям
 * движка этого аккаунта (AMEND-78). Пустой период — пустая таблица с прямой
 * подписью, никаких подставных чисел.
 *
 * Правило, которого нет в каталоге (`rule_id` пуст), остаётся в таблице под
 * своим кодом `<тип>/<id>`: срабатывание было, и прятать его нельзя.
 *
 * Таблица живёт в окне фиксированной высоты (≈ 10 строк) со своим скроллом и
 * прилипшей шапкой, как у SEON (AMEND-81 §2): страница не растягивается на
 * сотни правил. Футера с Prev/Next нет — следующая порция ручки подгружается,
 * когда окно докрутили до низа. Сортировка, фильтр или период — это новый
 * запрос: накопленное сбрасывается, окно встаёт в начало.
 */
export function AppliedRulesPanel() {
  const [period, setPeriod] = React.useState<Period | "custom">("30D")
  const [ruleType, setRuleType] = React.useState<string>(ALL)
  const [actionType, setActionType] = React.useState<string>(ALL)
  const [sortState, setSortState] = React.useState<SortState>({ sort: "times", order: "desc" })
  const [reload, setReload] = React.useState(0)

  const [items, setItems] = React.useState<RuleStat[]>([])
  const [loaded, setLoaded] = React.useState<Loaded | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  const windowRef = React.useRef<HTMLDivElement>(null)
  /** Запрос в полёте — первая страница или догрузка; новый набор фильтров его отменяет. */
  const inflight = React.useRef<AbortController | null>(null)

  const filters: Filters = React.useMemo(
    () => ({
      period: period === "custom" ? "30D" : period,
      type: ruleType === ALL ? undefined : (ruleType as RuleType),
      action: actionType === ALL ? undefined : actionType,
      sort: sortState.sort,
      order: sortState.order,
    }),
    [period, ruleType, actionType, sortState],
  )

  const fetchPage = React.useCallback(
    (page: number) => {
      inflight.current?.abort()
      const controller = new AbortController()
      inflight.current = controller
      setLoading(true)
      setError(null)

      void (async () => {
        try {
          const live = await listStats({ ...filters, page, page_size: PAGE_SIZE }, controller.signal)
          if (controller.signal.aborted) return
          setItems((previous) => {
            if (page === 1) return live.items
            // Между порциями могли прийти новые срабатывания и сдвинуть
            // страницы: одно правило не должно попасть в окно дважды.
            const seen = new Set(previous.map(rowKey))
            return [...previous, ...live.items.filter((stat) => !seen.has(rowKey(stat)))]
          })
          setLoaded({ page, pages: live.pages })
        } catch (cause) {
          if (controller.signal.aborted) return
          setError(cause instanceof Error ? cause.message : "Request failed")
        } finally {
          if (inflight.current === controller) {
            inflight.current = null
            setLoading(false)
          }
        }
      })()
    },
    [filters],
  )

  // Новый набор фильтров (или «обновить») — с чистого листа и с верха окна.
  React.useEffect(() => {
    setItems([])
    setLoaded(null)
    if (windowRef.current) windowRef.current.scrollTop = 0
    fetchPage(1)
    return () => {
      inflight.current?.abort()
      inflight.current = null
    }
  }, [fetchPage, reload])

  const hasMore = loaded !== null && loaded.page < loaded.pages

  const loadMore = React.useCallback(() => {
    if (inflight.current || !loaded || loaded.page >= loaded.pages) return
    fetchPage(loaded.page + 1)
  }, [fetchPage, loaded])

  const nearBottom = (element: HTMLElement) =>
    element.clientHeight > 0 && element.scrollHeight - element.scrollTop - element.clientHeight < LOAD_AHEAD_PX

  // Порция короче окна — скроллить нечем, и событие так и не придёт: догружаем сами.
  React.useEffect(() => {
    const element = windowRef.current
    if (element && hasMore && !error && nearBottom(element)) loadMore()
  }, [items, hasMore, error, loadMore])

  const actionTypes = React.useMemo(
    () => [...new Set(items.map((stat) => stat.action).filter((value): value is string => Boolean(value)))].sort(),
    [items],
  )

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="card-title">Applied rules statistics</h2>
          <button
            type="button"
            onClick={() => setReload((value) => value + 1)}
            aria-label="Reload statistics"
            className="text-fg-subtle transition-colors duration-150 hover:text-accent"
          >
            <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} strokeWidth={2} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Inactive reason="There is no statistics export endpoint in this console.">
            Export statistics
          </Inactive>
          <Inactive variant="primary" reason="Saving filter presets is not part of this console.">
            Save as default
          </Inactive>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <FilterSelect
          label="Action type"
          value={actionType}
          options={actionTypes.map((value) => ({ value, label: value }))}
          onChange={setActionType}
          emptyReason="No action types in the current result set."
        />
        <FilterSelect
          label="Rule type"
          value={ruleType}
          options={RULE_TYPES}
          onChange={setRuleType}
          emptyReason=""
        />
        <Inactive reason="Extra filters are not part of this console.">
          <ListFilter className="h-3.5 w-3.5" strokeWidth={2} />
          Add Filter
        </Inactive>

        <Segmented
          className="ml-auto"
          label="Statistics period"
          options={PERIOD_OPTIONS}
          value={period}
          onChange={setPeriod}
        />
      </div>

      <div
        ref={windowRef}
        role="region"
        aria-label="Applied rules"
        tabIndex={0}
        onScroll={(event) => {
          if (hasMore && !error && nearBottom(event.currentTarget)) loadMore()
        }}
        className="max-h-[440px] overflow-auto rounded-b [&_tbody_tr:last-child_td]:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
      >
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortHeader column="seon_id" state={sortState} onSort={setSortState} className="w-28">
                ID
              </SortHeader>
              <SortHeader column="rule_name" state={sortState} onSort={setSortState}>
                Rule name
              </SortHeader>
              <SortHeader column="times" state={sortState} onSort={setSortState} align="right" className="w-24">
                Times
              </SortHeader>
              <SortHeader column="approved_pct" state={sortState} onSort={setSortState} align="right" className="w-32">
                <Dot tone="approve" /> Approved %
              </SortHeader>
              <SortHeader column="reviewed_pct" state={sortState} onSort={setSortState} align="right" className="w-32">
                <Dot tone="review" /> Reviewed %
              </SortHeader>
              <SortHeader column="declined_pct" state={sortState} onSort={setSortState} align="right" className="w-32">
                <Dot tone="decline" /> Declined %
              </SortHeader>
              <SortHeader column="last_trigger_date" state={sortState} onSort={setSortState} className="w-40">
                Last trigger date
              </SortHeader>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading && items.length === 0 &&
              Array.from({ length: 6 }, (_, row) => (
                <TableRow key={row} className="hover:bg-transparent">
                  {Array.from({ length: 7 }, (_, column) => (
                    <TableCell key={column}>
                      <Skeleton className="h-3 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && error && items.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="px-4 py-10 text-center">
                  <p className="flex items-center justify-center gap-2 text-sm font-medium text-decline">
                    <TriangleAlert className="h-4 w-4" strokeWidth={2} />
                    {error}
                  </p>
                  <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && items.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="px-4 py-10 text-center text-sm text-fg-muted">
                  No rule triggered in this period.
                </TableCell>
              </TableRow>
            )}

            {items.map((stat) => (
              // Ключ — код правила, а не `rule_id`: у правил вне каталога он пуст.
              <TableRow key={`${stat.rule_type}/${stat.seon_id}`}>
                <TableCell className="text-fg-muted">{stat.seon_id}</TableCell>
                {/* Нижняя граница ширины: на планшете колонка иначе сжималась до
                    трёх-четырёх строк на имя, и в окно влезало пять правил. */}
                <TableCell className="min-w-[16rem] text-fg">
                  {stat.rule_name || (
                    <span className="text-fg-muted" title="Not in the rule catalog">
                      {stat.rule_type}/{stat.seon_id}
                    </span>
                  )}
                </TableCell>
                <TableNumeric>{formatInt(stat.times)}</TableNumeric>
                <TableNumeric>{formatPercent(stat.approved_pct)}</TableNumeric>
                <TableNumeric>{formatPercent(stat.reviewed_pct)}</TableNumeric>
                <TableNumeric>{formatPercent(stat.declined_pct)}</TableNumeric>
                <TableCell className="whitespace-nowrap text-fg-muted">
                  {formatDateFull(stat.last_trigger_date)}
                </TableCell>
              </TableRow>
            ))}

            {items.length > 0 && loading && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="py-3 text-center text-xs text-fg-muted" aria-live="polite">
                  Loading more rules…
                </TableCell>
              </TableRow>
            )}

            {items.length > 0 && !loading && error && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="py-3 text-center">
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-decline">
                    <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2} />
                    {error}
                  </span>
                  <Button size="sm" className="ml-3" onClick={loadMore}>
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

/** Точка у заголовка колонки — тот же цвет исхода, что в шапке образца. */
function Dot({ tone }: { tone: "approve" | "review" | "decline" }) {
  return (
    <span
      aria-hidden
      className={
        tone === "approve"
          ? "inline-block h-1.5 w-1.5 rounded-full bg-approve"
          : tone === "review"
            ? "inline-block h-1.5 w-1.5 rounded-full bg-review"
            : "inline-block h-1.5 w-1.5 rounded-full bg-decline"
      }
    />
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  emptyReason,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
  emptyReason: string
}) {
  if (options.length === 0) {
    return <Inactive reason={emptyReason}>{label}</Inactive>
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="min-w-[132px]" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: all</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
